import { clientStorage } from "@/lib/clientStorage";
import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { createPageUrl } from "@/utils";
import { 
  ArrowLeft, Upload, FileText, Loader2, CheckCircle2, AlertCircle, 
  RotateCcw, Copy, Save, Trash2, FolderOpen, Download, TrendingUp, 
  Briefcase, Users, CheckSquare, Search, Printer, CalendarDays
} from "lucide-react";
import { parseLacwDiaryFileStandalone } from "@/lib/standaloneServices";
import BillingSummary from "@/components/billing/BillingSummary";
import { DiaryCodesLegend } from "@/components/DiaryCodes";
import { getPortalToolPath } from "@/lib/portalToolRoutes";
import ClientGroup from "@/components/billing/ClientGroup";
import AppearanceRow from "@/components/billing/AppearanceRow";
import { BILLING_PARSER_VERSION, ACTION_OPTIONS, ACTION_STYLE, APPEARANCE_TYPE_OPTIONS, HELD_ACTIONS, billingFeeFor, billingAppearanceType, isClaimedAction, isLMarked, lacwFundedDefaults, renameBillingActions, REPORT_ACTION_OPTIONS } from "@/lib/billingConstants";
import { appearanceDate, feeSlipLinks, matchFeeSlips } from "@/lib/feeSlipMatching";
import BillingPrintSheet, { BILLING_PRINT_CSS } from "@/components/billing/BillingPrintSheet";
import AtlasComparePanel from "@/components/billing/AtlasComparePanel";
import PrepClaimsPanel from "@/components/billing/PrepClaimsPanel";
import { courtLevelForCourt, newPrepClaim } from "@/lib/prepClaims";
import LawyerQuestionsPanel from "@/components/billing/LawyerQuestionsPanel";
import { parseAtlasClaimsRows } from "@/lib/atlasClaims";
import BillingReportPanel, { REPORT_TYPES, buildBillingReport, formatMoney } from "@/components/billing/BillingReportPanel";
import {
  mergePendingAidItems,
  pendingAidKey,
  samePendingAidList,
  threeWayMergePendingAid,
} from "@/lib/pendingAidMerge";
import { toast } from "sonner";
import { useCloudDrafts } from "@/hooks/useCloudDrafts";
import GrantRemindersPanel from "@/components/GrantRemindersPanel";
import { diaryReminders } from "@/lib/grantReminders";
import { listLocalMatters, syncMattersWithPortal } from "@/lib/aidPlannerStore";
import { getClaimAvailabilityTable } from "@/lib/aidPlannerCriminalFees";
import {
  AID_FILTERS,
  AID_WEEK_FORM_TYPE,
  HELD_LISTS_DRAFT_ID,
  HELD_LISTS_FORM_TYPE,
  aidFor,
  aidStats,
  aidSuggestions as buildAidSuggestions,
  applySuggestion,
  diaryRow,
  diaryRows,
  fillAidFromPlanner,
  matchesAidFilter,
  publishedAidWeek,
  publishedHeldLists,
  weekPastRetention,
} from "@/lib/billingAidStatus";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import {
  getPortalRequestHeaders,
  OUTLOOK_BILLING_ADMIN_READ_BRAND,
  OUTLOOK_BILLING_BRAND,
  OUTLOOK_BILLING_FORM_TYPE,
  readLocalOutlookBillingIntake,
  removeLocalOutlookBillingDraft,
} from "@/lib/outlookBillingBridge";

const getDraftDateRange = (entries) => {
  if (!entries || entries.length === 0) return "Unnamed Week";
  
  const parsedDates = entries
    .map(e => {
      if (!e.date) return null;
      const parts = e.date.split('/');
      if (parts.length !== 3) return null;
      const d = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10) - 1;
      const y = parseInt(parts[2], 10);
      const dateObj = new Date(y, m, d);
      return isNaN(dateObj.getTime()) ? null : dateObj;
    })
    .filter(Boolean);
    
  if (parsedDates.length === 0) return "Unnamed Week";
  
  parsedDates.sort((a, b) => a - b);
  const minDate = parsedDates[0];
  const maxDate = parsedDates[parsedDates.length - 1];
  
  const pad = (n) => String(n).padStart(2, '0');
  
  const startStr = `${pad(minDate.getDate())}/${pad(minDate.getMonth() + 1)}`;
  const endStr = `${pad(maxDate.getDate())}/${pad(maxDate.getMonth() + 1)}/${maxDate.getFullYear()}`;
  
  return `${startStr} - ${endStr}`;
};

const appearanceDateSortValue = (value = "") => {
  const match = String(value).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return Number.MAX_SAFE_INTEGER;
  return new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1])).getTime();
};

const formatAppearanceDate = (value = "") => {
  const timestamp = appearanceDateSortValue(value);
  if (!Number.isFinite(timestamp) || timestamp === Number.MAX_SAFE_INTEGER) return value || "Date not provided";
  return new Date(timestamp).toLocaleDateString("en-AU", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const PENDING_AID_DRAFT_ID = "00000000-0000-4000-8000-00000000a1d0";

const mergeOutlookIntakeDrafts = (remoteDrafts = [], localDrafts = []) => {
  const merged = new Map();
  [
    ...remoteDrafts.map((draft) => ({ ...draft, _intakeOrigin: "portal" })),
    ...localDrafts.map((draft) => ({ ...draft, _intakeOrigin: "local" })),
  ].forEach((draft) => {
    const sourceId = draft?.form_data?.entry?.source_id;
    const key = sourceId || draft.id;
    if (key && !merged.has(key)) merged.set(key, draft);
  });
  return Array.from(merged.values()).sort((a, b) =>
    String(b.form_data?.submitted_at || b.updated_at || "").localeCompare(
      String(a.form_data?.submitted_at || a.updated_at || "")
    )
  );
};

// The lists that carry appearances over between billing weeks. All live in the shared
// Aid Pending queue; an item's `action` decides which list shows it.
const HELD_LISTS = {
  "Aid pending": {
    title: "Aid Pending",
    description: "These matters stay here across billing weeks until you change their action after aid is approved.",
    empty: "No matters are waiting for aid",
    updateLabel: "Update when approved",
    banner: "border-amber-200 bg-amber-50",
    heading: "text-amber-950",
    icon: "text-amber-600",
    body: "text-amber-800",
    pill: "border-amber-300 text-amber-800",
    card: "border-amber-200",
  },
  "To be claimed": {
    title: "To be claimed",
    description: "Appearances to claim another time, for example because aid is not approved yet or has not been applied for. They stay here across billing weeks until you change their action.",
    empty: "Nothing is waiting to be claimed",
    updateLabel: "Update when ready to claim",
    banner: "border-cyan-200 bg-cyan-50",
    heading: "text-cyan-950",
    icon: "text-cyan-600",
    body: "text-cyan-800",
    pill: "border-cyan-300 text-cyan-800",
    card: "border-cyan-200",
  },
  "Counsel appeared": {
    title: "Awaiting fee slip",
    description: "Appearances where counsel appeared. Each stays here until counsel's fee slip arrives by email: the portal matches incoming fee slips to the client automatically. Clerk emails that don't name the client can't be matched, so tick those off yourself.",
    empty: "No fee slips are outstanding",
    updateLabel: "Action",
    banner: "border-sky-200 bg-sky-50",
    heading: "text-sky-950",
    icon: "text-sky-600",
    body: "text-sky-800",
    pill: "border-sky-300 text-sky-800",
    card: "border-sky-200",
  },
};

const counselNameFor = (item = {}) =>
  String(item.counsel_briefed || "").trim()
  || String(item.appearance_type || "").replace(/^counsel appeared\s*-?\s*/i, "").trim();

// On Awaiting fee slip and not yet ticked off. Ticked-off matters stay in the queue, hidden,
// so the catch-up below does not put them back.
const isAwaitingFeeSlip = (item = {}) => item.action === "Counsel appeared" && !item.feeSlipDoneAt;

const daysSince = (date) => {
  const from = appearanceDate(date);
  return from ? Math.max(0, Math.floor((Date.now() - from.getTime()) / 86400000)) : null;
};

export default function LACWBilling({ isPortalMode = false }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [actions, setActions] = useState({});
  // A week saved before a type was renamed (src/lib/billingConstants.js) shows, and saves, its new name.
  useEffect(() => {
    const renamed = renameBillingActions(actions);
    if (renamed !== actions) setActions(renamed);
  }, [actions]);
  const [notes, setNotes] = useState({});
  const [fees, setFees] = useState({});
  // Each appearance's legal aid status (what the LACW Aid Status Diary held), by id.
  const [aid, setAid] = useState({});
  const [aidFilter, setAidFilter] = useState("All");
  const [autoSavedAt, setAutoSavedAt] = useState(null);
  const [autoSaveFailed, setAutoSaveFailed] = useState(false);
  const [recoveredDraft, setRecoveredDraft] = useState(null);
  const [pendingAidItems, setPendingAidItems] = useState([]);
  const [pendingAidLoaded, setPendingAidLoaded] = useState(false);
  const [pendingAidSaving, setPendingAidSaving] = useState(false);
  const [pendingAidSyncError, setPendingAidSyncError] = useState(false);
  // `updated_at` of the stored queue as we last saw it, sent back as `expected_updated_at`
  // so the server can reject a write based on a version someone has since replaced.
  const pendingAidVersionRef = useRef(null);
  // The queue contents at that version - the common ancestor for a three-way merge.
  const pendingAidBaseRef = useRef([]);
  const [outlookIntake, setOutlookIntake] = useState([]);
  const [outlookIntakeLoading, setOutlookIntakeLoading] = useState(true);
  const [outlookIntakeSyncError, setOutlookIntakeSyncError] = useState(false);
  const fileRef = useRef();
  const archiveInputRef = useRef();
  const atlasInputRef = useRef();

  // Navigation and Filtering state for Reporting Dashboard
  const [activeView, setActiveView] = useState("billing");
  const [reportType, setReportType] = useState("overview");
  const [filterWeek, setFilterWeek] = useState("all");
  const [filterAction, setFilterAction] = useState("all");
  const [filterLawyer, setFilterLawyer] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const { drafts: cloudDrafts, fetchDrafts, saveDraft: saveBillingDraft, deleteDraft: apiDeleteDraft, isLoading: isDraftLoading } = useCloudDrafts();

  // Data aggregation for Reporting
  const aggregatedEntries = useMemo(() => {
    if (!cloudDrafts) return [];
    const allEntries = [];
    cloudDrafts.forEach(draft => {
      const data = draft.form_data;
      if (data && data.result && data.result.entries) {
        const draftActions = data.actions || {};
        const draftNotes = data.notes || {};
        const draftFees = data.fees || {};
        const draftName = draft.draft_name || "Unnamed Week";
        data.result.entries.forEach(entry => {
          const fee = billingFeeFor(entry, draftActions[entry.id], draftFees[entry.id]);
          allEntries.push({
            ...entry,
            weekLabel: draftName,
            action: draftActions[entry.id] || "No Action",
            note: draftNotes[entry.id] || draftNotes[entry.client_name] || "",
            billingType: billingAppearanceType(entry, draftActions[entry.id]) || "Not recorded",
            fee: fee != null && !Number.isNaN(fee) ? fee : null,
          });
        });
      }
    });
    return allEntries;
  }, [cloudDrafts]);

  // Unique weeks for filters
  const uniqueWeeks = useMemo(() => {
    if (!cloudDrafts) return [];
    return cloudDrafts.map(d => d.draft_name).filter(Boolean);
  }, [cloudDrafts]);

  // Unique lawyers for filters
  const uniqueLawyers = useMemo(() => {
    const lawyers = new Set();
    aggregatedEntries.forEach(e => {
      if (e.lawyer_initials) {
        lawyers.add(e.lawyer_initials.trim().toUpperCase());
      }
    });
    return Array.from(lawyers).sort();
  }, [aggregatedEntries]);

  // Filtered entries for reporting
  const filteredEntries = useMemo(() => {
    return aggregatedEntries.filter(e => {
      if (filterWeek !== "all" && e.weekLabel !== filterWeek) return false;
      if (filterAction !== "all" && e.action !== filterAction) return false;
      if (filterLawyer !== "all" && e.lawyer_initials?.trim().toUpperCase() !== filterLawyer) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const client = (e.client_name || "").toLowerCase();
        const court = (e.court || "").toLowerCase();
        const outcome = (e.outcome || "").toLowerCase();
        const note = (e.note || "").toLowerCase();
        const type = (e.appearance_type || "").toLowerCase();
        if (!client.includes(q) && !court.includes(q) && !outcome.includes(q) && !note.includes(q) && !type.includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [aggregatedEntries, filterWeek, filterAction, filterLawyer, searchQuery]);

  // KPI Calculations
  const stats = useMemo(() => {
    const total = filteredEntries.length;
    const claimedOrBilled = filteredEntries.filter(e => isClaimedAction(e.action)).length;
    const counsel = filteredEntries.filter(e => e.action === "Counsel appeared" || e.counsel_briefed).length;
    const funded = filteredEntries.filter(e => e.action === "LACW funded").length;
    
    return { total, claimedOrBilled, counsel, funded };
  }, [filteredEntries]);

  // Action status counts
  const actionCounts = useMemo(() => {
    const counts = {};
    ACTION_OPTIONS.forEach(opt => {
      if (opt.value) counts[opt.value] = 0;
    });
    counts["No Action"] = 0;
    
    filteredEntries.forEach(e => {
      const act = e.action || "No Action";
      counts[act] = (counts[act] || 0) + 1;
    });
    
    return Object.entries(counts)
      .map(([name, value]) => ({ name, value }))
      .filter(item => item.value > 0);
  }, [filteredEntries]);

  // Lawyer counts
  const lawyerCounts = useMemo(() => {
    const counts = {};
    filteredEntries.forEach(e => {
      const init = (e.lawyer_initials || "Unknown").trim().toUpperCase();
      counts[init] = (counts[init] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [filteredEntries]);

  const report = useMemo(
    () => buildBillingReport(reportType, filteredEntries, uniqueWeeks),
    [reportType, filteredEntries, uniqueWeeks]
  );

  const handleExportCSV = () => {
    if (!filteredEntries || filteredEntries.length === 0) {
      toast.error("No data to export");
      return;
    }
    const grouped = report.type === "grouped";
    const headers = grouped
      ? [report.groupLabel, "Appearances", "Claimed", "Still to claim", "Total fees"]
      : ["Date", "Week", "Client Name", "Court", "Appearance Type", "Lawyer Initials", "Action Status", "Fee", "Suggested ATLAS Claim", "Outcome", "Notes"];

    const rows = grouped ? report.rows.map(r => [r.label, r.count, r.claimed, r.outstanding, r.total]) : (report.type === "list" ? report.entries : filteredEntries).map(e => [
      e.date || "",
      e.weekLabel || "",
      e.client_name || "",
      e.court || "",
      e.billingType || "",
      e.lawyer_initials || "",
      e.action || "No Action",
      e.fee ?? "",
      e.atlas_claim_type || "",
      e.outcome || "",
      e.note || ""
    ]);
    
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))
    ].join("\n");
    
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `LACW_Billing_${(REPORT_TYPES.find(r => r.value === reportType)?.label || "Report").replace(/[^a-z0-9]+/gi, "_")}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV file exported successfully!");
  };

  const handleLoadDraftFromReports = (weekName) => {
    const draft = cloudDrafts.find(d => d.draft_name === weekName);
    if (draft) {
      handleLoadDraft(draft);
      setActiveView("billing");
    } else {
      toast.error(`Could not find week: ${weekName}`);
    }
  };


  useEffect(() => {
    fetchDrafts("billing_sheet", "admin");
    // Another tab may have saved a week while this one was in the background.
    const refresh = () => fetchDrafts("billing_sheet", "admin");
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [fetchDrafts]);

  const loadOutlookIntake = useCallback(async () => {
    setOutlookIntakeLoading(true);
    const localDrafts = readLocalOutlookBillingIntake();
    try {
      const brands = [OUTLOOK_BILLING_ADMIN_READ_BRAND, OUTLOOK_BILLING_BRAND];
      const responses = await Promise.all(brands.map((brand) => {
        const params = new URLSearchParams({ brand, form_type: OUTLOOK_BILLING_FORM_TYPE });
        return fetch(`/api/vals-drafts?${params}`, {
          credentials: "include",
          headers: getPortalRequestHeaders(),
        });
      }));
      const failedResponse = responses.find((response) => !response.ok);
      if (failedResponse) throw new Error(`Outlook intake returned ${failedResponse.status}`);
      const remoteDrafts = (await Promise.all(responses.map((response) => response.json()))).flat();
      setOutlookIntake(mergeOutlookIntakeDrafts(remoteDrafts, localDrafts));
      setOutlookIntakeSyncError(false);
    } catch (err) {
      console.warn("Using local Outlook billing intake:", err);
      setOutlookIntake(mergeOutlookIntakeDrafts([], localDrafts));
      setOutlookIntakeSyncError(true);
    } finally {
      setOutlookIntakeLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOutlookIntake();
  }, [loadOutlookIntake]);

  // Fee slips filed by the email intake, for matching against Awaiting fee slip.
  const [intakeFeeSlips, setIntakeFeeSlips] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/email-intake?brand=lacw", { credentials: "include", cache: "no-store" })
      .then(response => (response.ok ? response.json() : Promise.reject(new Error(`Email intake returned ${response.status}`))))
      .then(rows => { if (!cancelled) setIntakeFeeSlips(Array.isArray(rows) ? rows : []); })
      .catch(err => console.warn("Fee slips could not be loaded for matching:", err));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!pendingAidLoaded || !intakeFeeSlips) return;
    const awaiting = pendingAidItems.filter(isAwaitingFeeSlip);
    const matches = matchFeeSlips(awaiting, intakeFeeSlips);
    if (matches.size === 0) return;
    setPendingAidItems(prev => prev.map(item =>
      isAwaitingFeeSlip(item) && !item.feeSlip && matches.has(item.pendingKey)
        ? { ...item, feeSlip: { ...matches.get(item.pendingKey), matchedAt: new Date().toISOString() } }
        : item
    ));
    toast.success(`${matches.size} fee slip${matches.size === 1 ? "" : "s"} received for Awaiting fee slip matters.`);
  }, [pendingAidLoaded, intakeFeeSlips, pendingAidItems]);

  // Catch-up: rows already set to Counsel appeared - in saved weeks or the week on screen -
  // before the Awaiting fee slip list existed, or whose list entry was never created.
  useEffect(() => {
    if (!pendingAidLoaded) return;
    const found = new Map();
    const collect = (entries = [], entryActions = {}) => entries.forEach(entry => {
      if (entryActions[entry.id] !== "Counsel appeared") return;
      const key = pendingAidKey(entry);
      if (key && !found.has(key)) found.set(key, entry);
    });
    (cloudDrafts || []).forEach(draft => collect(draft.form_data?.result?.entries, draft.form_data?.actions));
    collect(result?.entries, actions);
    if (found.size === 0) return;

    setPendingAidItems(prev => {
      const known = new Set(prev.map(item => item.pendingKey || pendingAidKey(item)));
      const missing = [...found].filter(([key]) => !known.has(key));
      if (missing.length === 0) return prev;
      const now = new Date().toISOString();
      return mergePendingAidItems(prev, missing.map(([key, entry]) => ({
        ...entry,
        pendingKey: key,
        action: "Counsel appeared",
        note: "",
        pendingSince: now,
      })));
    });
  }, [pendingAidLoaded, cloudDrafts, result, actions]);

  const handleFeeSlipDone = (item) => {
    setPendingAidItems(prev => prev.map(candidate => candidate.pendingKey === item.pendingKey
      ? { ...candidate, feeSlipDoneAt: new Date().toISOString() }
      : candidate));
    toast.success(`${item.client_name || "Matter"} removed from Awaiting fee slip.`);
  };

  const handleFeeSlipWrongMatch = (item) => {
    setPendingAidItems(prev => prev.map(candidate => candidate.pendingKey === item.pendingKey
      ? { ...candidate, feeSlip: undefined, rejectedFeeSlips: [...(candidate.rejectedFeeSlips || []), item.feeSlip?.id].filter(Boolean) }
      : candidate));
  };

  useEffect(() => {
    let cancelled = false;
    const loadPendingAid = async () => {
      let sessionItems = [];
      try {
        sessionItems = JSON.parse(clientStorage.getItem("lacw_billing_pending_aid") || "[]");
      } catch {}

      try {
        const response = await fetch("/api/vals-drafts?brand=admin&form_type=lacw_pending_aid", { credentials: "include" });
        if (!response.ok) throw new Error(`Pending aid service returned ${response.status}`);
        const drafts = await response.json();
        const remoteItems = Array.isArray(drafts?.[0]?.form_data?.items) ? drafts[0].form_data.items : [];
        if (!cancelled) {
          // Record the version and contents we loaded. These are the common ancestor for
          // resolving a later save conflict, and the version the server checks against.
          pendingAidVersionRef.current = drafts?.[0]?.updated_at || null;
          pendingAidBaseRef.current = remoteItems;
          setPendingAidItems(mergePendingAidItems(remoteItems, sessionItems));
          setPendingAidSyncError(false);
        }
      } catch (err) {
        console.warn("Using secure session recovery for Aid Pending queue:", err);
        if (!cancelled) {
          setPendingAidItems(mergePendingAidItems(sessionItems));
          setPendingAidSyncError(true);
        }
      } finally {
        if (!cancelled) setPendingAidLoaded(true);
      }
    };
    loadPendingAid();
    return () => { cancelled = true; };
  }, []);

  // Saves the queue, resolving a concurrent edit rather than overwriting it.
  //
  // The server rejects the write with 409 when `expected_updated_at` no longer matches the
  // stored row, which means someone else saved since we loaded. We then re-read their
  // version, three-way merge it with ours, and try again. Retries are bounded because each
  // round can lose another race; two rounds covers realistic contention on this queue.
  const savePendingAidQueue = useCallback(async (items) => {
    let pending = items;

    for (let attempt = 0; attempt < 3; attempt += 1) {
      const body = {
        id: PENDING_AID_DRAFT_ID,
        form_type: "lacw_pending_aid",
        draft_name: "Aid Pending Queue",
        brand: "admin",
        form_data: { items: pending },
      };
      // Omitted on the very first save, when no row exists yet.
      if (pendingAidVersionRef.current) body.expected_updated_at = pendingAidVersionRef.current;

      const response = await fetch("/api/vals-drafts", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (response.status === 409) {
        const refreshed = await fetch("/api/vals-drafts?brand=admin&form_type=lacw_pending_aid", { credentials: "include" });
        if (!refreshed.ok) throw new Error(`Pending aid reload returned ${refreshed.status}`);
        const drafts = await refreshed.json();
        const theirs = Array.isArray(drafts?.[0]?.form_data?.items) ? drafts[0].form_data.items : [];
        const merged = threeWayMergePendingAid(pendingAidBaseRef.current, pending, theirs);

        pendingAidVersionRef.current = drafts?.[0]?.updated_at || null;
        pendingAidBaseRef.current = theirs;
        pending = merged;
        setPendingAidItems(merged);
        toast.info("Another user changed the Aid Pending queue. Their changes have been merged with yours.");
        continue;
      }

      if (!response.ok) throw new Error(`Pending aid save returned ${response.status}`);

      const saved = await response.json();
      pendingAidVersionRef.current = saved?.updated_at || null;
      pendingAidBaseRef.current = pending;
      return;
    }

    throw new Error("Pending aid queue could not be saved after repeated conflicts");
  }, []);

  useEffect(() => {
    if (!pendingAidLoaded) return;
    clientStorage.setItem("lacw_billing_pending_aid", JSON.stringify(pendingAidItems));
    // A merge calls setPendingAidItems, which re-runs this effect. Without this guard that
    // would post the same queue straight back and could ping-pong against another editor.
    if (samePendingAidList(pendingAidItems, pendingAidBaseRef.current)) return;
    const timer = setTimeout(async () => {
      setPendingAidSaving(true);
      try {
        await savePendingAidQueue(pendingAidItems);
        setPendingAidSyncError(false);
      } catch (err) {
        console.warn("Aid Pending queue remains in secure session recovery:", err);
        setPendingAidSyncError(true);
      } finally {
        setPendingAidSaving(false);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [pendingAidItems, pendingAidLoaded, savePendingAidQueue]);

  useEffect(() => {
    const autosave = clientStorage.getItem("lacw_billing_autosave");
    if (autosave) {
      try {
        setRecoveredDraft(JSON.parse(autosave));
      } catch {}
    }
  }, []);

  useEffect(() => {
    if (!result) return;
    const timer = setTimeout(() => {
      const autosaveDraft = {
        fileName: file?.name || "Unnamed Diary",
        dateSaved: new Date().toISOString(),
        result,
        actions,
        notes,
        fees,
        aid,
        pendingAidItems,
      };
      // Only a recovery copy for this tab: failing here must not stop the week saving below.
      try {
        clientStorage.setItem("lacw_billing_autosave", JSON.stringify(autosaveDraft));
      } catch (err) {
        console.warn("Billing recovery copy could not be written:", err);
      }

      // Keep the saved week itself up to date, so nothing depends on pressing Save Week.
      // Save Week still downloads the backup file.
      saveBillingDraft({
        id: result.draftId,
        formType: "billing_sheet",
        draftName: getDraftDateRange(result.entries),
        brand: "admin",
        formData: {
          fileName: file?.name || "Unnamed Diary",
          result: { ...result, draftId: undefined },
          actions,
          notes,
          fees,
          aid,
          pendingAidItems,
        },
      })
        .then((saved) => {
          setAutoSavedAt(new Date());
          setAutoSaveFailed(false);
          if (saved?.id && saved.id !== result.draftId) {
            setResult((current) => current ? { ...current, draftId: saved.id } : current);
          }
        })
        .catch((err) => {
          console.warn("Billing week auto-save failed:", err);
          setAutoSaveFailed(true);
        });
    }, 2000);

    return () => clearTimeout(timer);
  }, [result, actions, notes, fees, aid, pendingAidItems, file?.name]);

  const handleSaveDraft = async () => {
    if (!result) {
      toast.error("Please process a diary PDF before saving.");
      return;
    }
    const draftName = getDraftDateRange(result.entries);

    try {
      const archive = {
        format: "cls-lacw-weekly-billing",
        version: 2,
        weekName: draftName,
        sourceFileName: file?.name || "",
        savedAt: new Date().toISOString(),
        result: { ...result, draftId: undefined },
        actions,
        notes,
        fees,
        aid,
        pendingAidItems,
      };
      // The backup below is the way out when the browser refuses the save, so a failure here
      // must not stop it downloading.
      let savedHere = true;
      try {
        const saved = await saveBillingDraft({
        id: result.draftId,
        formType: "billing_sheet",
        draftName,
        brand: "admin",
        formData: {
          fileName: file?.name || "Unnamed Diary",
          result: { ...result, draftId: undefined },
          actions,
          notes,
          fees,
          aid,
          pendingAidItems,
        },
      });
        setResult((current) => current ? { ...current, draftId: saved.id } : current);
        setAutoSaveFailed(false);
      } catch (err) {
        console.warn("Billing week could not be saved on this computer:", err);
        savedHere = false;
        setAutoSaveFailed(true);
      }
      const blob = new Blob([JSON.stringify(archive, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `LACW_Billing_${draftName.replace(/[^a-z0-9_-]+/gi, "_")}.lacw-week.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      if (savedHere) toast.success("Billing week saved on this computer and backed up to Downloads.");
      else toast.error("This computer refused the save, so the week is not in Saved Billing Weeks. A backup is in Downloads: keep it.");
    } catch (err) {
      toast.error(err.message || "Failed to save week");
    }
  };

  const handleLoadSavedWeek = async (event) => {
    const archiveFile = event.target.files?.[0];
    event.target.value = "";
    if (!archiveFile) return;

    try {
      const archive = JSON.parse(await archiveFile.text());
      if (archive?.format !== "cls-lacw-weekly-billing" || !Array.isArray(archive?.result?.entries)) {
        throw new Error("This is not a saved LACW billing week file.");
      }
      if (result && !window.confirm(`Replace the billing week currently on screen with "${archive.weekName || archiveFile.name}"?`)) return;

      const entries = archive.result.entries.map((entry, index) => ({
        ...entry,
        id: entry.id || ((typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") ? crypto.randomUUID() : `entry_${index}_${Date.now()}`),
        outcome: entry.outcome?.trim() || "Further information required.",
      }));
      const loadedResult = { ...archive.result };
      delete loadedResult.draftId;
      setResult({ ...loadedResult, entries });
      setActions(archive.actions || {});
      setNotes(archive.notes || {});
      setFees(archive.fees || {});
      setAid(archive.aid || {});
      setPendingAidItems(prev => mergePendingAidItems(prev, archive.pendingAidItems || []));
      setFile({ name: archive.sourceFileName || archive.weekName || archiveFile.name, size: 0, type: "application/pdf" });
      setError(null);
      toast.success(`Opened ${archive.weekName || archiveFile.name}`);
    } catch (err) {
      toast.error(err.message || "The saved billing week could not be opened.");
    }
  };


  const handleLoadDraft = (draft) => {
    const data = draft.form_data;
    if (!data) return;

    setResult({
      ...data.result,
      draftId: draft.id
    });
    setActions(data.actions || {});
    setNotes(data.notes || {});
    setFees(data.fees || {});
    setAid(data.aid || {});
    setPendingAidItems(prev => mergePendingAidItems(prev, data.pendingAidItems || []));
    setFile({ name: data.fileName || draft.draft_name || "Loaded Diary", size: 0, type: "application/pdf" });
    setError(null);
    toast.success(`Loaded draft: ${draft.draft_name}`);
  };

  const handleDeleteDraft = async (id) => {
    try {
      await apiDeleteDraft(id, "billing_sheet", "admin");
      toast.success("Saved upload deleted.");
      if (result?.draftId === id) {
        setResult(prev => {
          if (!prev) return null;
          const next = { ...prev };
          delete next.draftId;
          return next;
        });
      }
    } catch {
      toast.error("Failed to delete saved upload");
    }
  };

  const handleLoadAutosave = (draft) => {
    setResult(draft.result);
    setActions(draft.actions || {});
    setNotes(draft.notes || {});
    setFees(draft.fees || {});
    setAid(draft.aid || {});
    setPendingAidItems(prev => mergePendingAidItems(prev, draft.pendingAidItems || []));
    setFile({ name: draft.fileName, size: 0, type: "application/pdf" });
    setError(null);
    setRecoveredDraft(null);
    toast.success(`Restored autosave for ${draft.fileName}`);
  };

  const handleActionChange = (id, value) => {
    setActions(prev => ({ ...prev, [id]: value }));
    if (APPEARANCE_TYPE_OPTIONS.includes(value)) {
      // A new appearance type has its own listed fee, so the typed main amount is dropped.
      // Extra fee lines were added on purpose and stay.
      setFees(prev => (prev[id] ? { ...prev, [id]: prev[id].extra?.length ? { extra: prev[id].extra } : {} } : prev));
    }
    const entry = result?.entries?.find(item => item.id === id);
    if (!entry) return;
    const key = pendingAidKey(entry);
    if (HELD_ACTIONS.includes(value)) {
      setPendingAidItems(prev => mergePendingAidItems(prev, [{
        ...entry,
        pendingKey: key,
        action: value,
        note: notes[id] || "",
        feeSlipDoneAt: undefined,
        pendingSince: prev.find(item => item.pendingKey === key)?.pendingSince || new Date().toISOString(),
      }]));
      toast.success(`${entry.client_name || "Matter"} added to ${HELD_LISTS[value].title}.`);
    } else {
      setPendingAidItems(prev => prev.filter(item => item.pendingKey !== key));
    }
  };

  const handleFeeChange = (id, patch) => {
    setFees(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };

  const handleAidChange = (id, patch) => {
    setAid(prev => ({ ...prev, [id]: { ...aidFor(prev, id), ...patch } }));
  };

  const handleUseAidSuggestion = (id, suggestion) => {
    setAid(prev => ({ ...prev, [id]: applySuggestion(aidFor(prev, id), suggestion) }));
  };

  const handleNoteChange = (id, value) => {
    setNotes(prev => ({ ...prev, [id]: value }));
    const entry = result?.entries?.find(item => item.id === id);
    if (entry) {
      const key = pendingAidKey(entry);
      setPendingAidItems(prev => prev.map(item => item.pendingKey === key ? { ...item, note: value } : item));
    }
  };

  const handlePendingActionChange = (item, value) => {
    const currentList = item.action || "Aid pending";
    if (value === currentList) return;
    const currentEntry = result?.entries?.find(entry => pendingAidKey(entry) === item.pendingKey);
    if (currentEntry) {
      setActions(prev => ({ ...prev, [currentEntry.id]: value }));
    }
    if (HELD_ACTIONS.includes(value)) {
      // Moving between Aid Pending and To be claimed keeps the matter parked.
      setPendingAidItems(prev => prev.map(candidate => candidate.pendingKey === item.pendingKey ? { ...candidate, action: value } : candidate));
      toast.success(`${item.client_name || "Matter"} moved from ${HELD_LISTS[currentList].title} to ${HELD_LISTS[value].title}.`);
      return;
    }
    setPendingAidItems(prev => prev.filter(candidate => candidate.pendingKey !== item.pendingKey));
    toast.success(`${item.client_name || "Matter"} removed from ${HELD_LISTS[currentList].title} and marked ${value || "actioned"}.`);
  };

  const handlePendingNoteChange = (item, value) => {
    setPendingAidItems(prev => prev.map(candidate => candidate.pendingKey === item.pendingKey ? { ...candidate, note: value } : candidate));
    if (result?.entries?.some(entry => entry.id === item.id)) {
      setNotes(prev => ({ ...prev, [item.id]: value }));
    }
  };

  const handleImportOutlookEntry = async (draft) => {
    const entry = draft?.form_data?.entry;
    if (!entry) {
      toast.error("This Outlook intake item has no billing entry.");
      return;
    }
    if (result?.entries?.some((item) =>
      (entry.source_id && item.source_id === entry.source_id) || item.id === entry.id
    )) {
      toast.error("This Outlook appearance is already in the current billing week.");
      return;
    }

    setResult((current) => ({
      ...(current || { summary: "Billing entries started from Outlook Intake" }),
      entries: [...(current?.entries || []), entry],
    }));
    if (entry.grant_type === "LACW") {
      setActions((current) => ({ ...current, [entry.id]: "LACW funded" }));
    }
    setFile((current) => current || {
      name: "Outlook Intake",
      size: 0,
      type: "application/outlook-intake",
    });
    setActiveView("billing");

    try {
      if (draft._intakeOrigin === "local") {
        removeLocalOutlookBillingDraft(draft.id);
      } else {
        const response = await fetch(`/api/vals-drafts?id=${encodeURIComponent(draft.id)}`, {
          method: "DELETE",
          credentials: "include",
          headers: getPortalRequestHeaders(),
        });
        if (!response.ok) throw new Error(`Intake archive returned ${response.status}`);
      }
      setOutlookIntake((current) => current.filter((item) => item.id !== draft.id));
      toast.success(`${entry.client_name || "Outlook appearance"} added to the billing processor.`);
    } catch (err) {
      console.warn("Outlook entry imported but intake item could not be archived:", err);
      toast.warning("Entry added to billing, but the intake item could not be archived.");
    }
  };

  const handleFile = (f) => {
    if (!f || f.type !== "application/pdf") {
      setError("Please upload a PDF file.");
      return;
    }
    setFile(f);
    setResult(null);
    setError(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files[0]);
  };

  const handleProcess = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const extracted = await parseLacwDiaryFileStandalone(file);

      if (!extracted?.entries?.length) {
        const debugText = extracted?.debug
          ? ` [debug source=${extracted.debug.selectedSource}, pdfJs=${extracted.debug.pdfJsTextLength}, stream=${extracted.debug.streamTextLength}, ocr=${extracted.debug.ocrTextLength}, normalized=${extracted.debug.normalizedLength}, context=${extracted.debug.contextEntriesCount}, merged=${extracted.debug.mergedLineCount}, fallback=${extracted.debug.fallbackEntriesCount}]`
          : "";
        setError(`No entries extracted. The PDF may be a scanned image or unreadable. Try a different file.${debugText}`);
        setLoading(false);
        return;
      }

      const entriesWithId = extracted.entries.map((entry, idx) => ({
        ...entry,
        id: (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") ? crypto.randomUUID() : `entry_${idx}_${Date.now()}`
      }));

      setResult({ ...extracted, entries: entriesWithId });
      setNotes({});
      setFees({});
      setAid({});
      // The diary reader marks counsel briefed / appeared as "Counsel appeared - <name>";
      // those go straight onto Awaiting fee slip.
      const counselEntries = entriesWithId.filter(entry => /^counsel appeared\b/i.test(entry.appearance_type || ""));
      if (counselEntries.length) {
        setActions(prev => ({ ...prev, ...Object.fromEntries(counselEntries.map(entry => [entry.id, "Counsel appeared"])) }));
        setPendingAidItems(prev => mergePendingAidItems(prev, counselEntries.map(entry => {
          const key = pendingAidKey(entry);
          return {
            ...entry,
            pendingKey: key,
            action: "Counsel appeared",
            note: "",
            pendingSince: prev.find(item => item.pendingKey === key)?.pendingSince || new Date().toISOString(),
          };
        })));
      }
      setLoading(false);
    } catch (err) {
      setError("Error processing diary: " + (err?.message || "Unknown error"));
      setLoading(false);
    }
  };

  const handleCopyAll = () => {
    if (!result?.entries?.length) return;
    const text = result.entries.map((e, i) =>
      `#${i + 1} | ${e.date || ""} | ${e.client_name || ""} | ${e.court || ""} | ${e.appearance_type || ""} | ATLAS: ${e.atlas_claim_type || ""} | Outcome: ${e.outcome || ""}`
    ).join("\n");
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePrint = () => {
    if (claimsWithoutFee.length > 0) {
      const names = claimsWithoutFee.slice(0, 8).map(entry => `- ${entry.date || ""} ${entry.client_name || "Client not named"} (${actions[entry.id]})`).join("\n");
      const more = claimsWithoutFee.length > 8 ? `\n...and ${claimsWithoutFee.length - 8} more` : "";
      const proceed = window.confirm(
        `${claimsWithoutFee.length} claimed appearance${claimsWithoutFee.length === 1 ? " has" : "s have"} no fee allocated:\n\n${names}${more}\n\nThey will print without a fee and are left out of the totals. Print anyway?`
      );
      if (!proceed) return;
    }
    window.print();
  };

  // An ATLAS claims export, compared with this week. Kept on the week (result.atlasClaims)
  // so it is saved, auto-saved and reopened with it.
  const handleAtlasUpload = async (event) => {
    const upload = event.target.files?.[0];
    event.target.value = "";
    if (!upload) return;
    try {
      const XLSX = await import("xlsx");
      const workbook = XLSX.read(await upload.arrayBuffer(), { cellDates: true });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: null });
      const { claims } = parseAtlasClaimsRows(rows);
      if (claims.length === 0) throw new Error("No claims were found in that spreadsheet.");
      setResult(current => current ? {
        ...current,
        atlasClaims: {
          fileName: upload.name,
          uploadedAt: new Date().toISOString(),
          claims: claims.map(claim => ({
            ...claim,
            claimedOn: claim.claimedOn ? claim.claimedOn.toISOString() : null,
            appearanceOn: claim.appearanceOn ? claim.appearanceOn.toISOString() : null,
          })),
        },
      } : current);
      toast.success(`Compared ${claims.length} ATLAS claims with this week.`);
    } catch (err) {
      toast.error(err?.message || "That spreadsheet could not be read.");
    }
  };

  // Preparation claims for the week on screen (src/lib/prepClaims.js), kept on the week
  // (result.prepClaims) so they save, auto-save and reopen with it.
  const prepClaims = result?.prepClaims || [];
  const setPrepClaims = (lines) => setResult(current => (current ? { ...current, prepClaims: lines } : current));
  const addPrepClaim = (fields) => {
    if (!result) {
      toast.error("Open or process this week's billing sheet first, then add the prep claim.");
      return;
    }
    setResult(current => (current ? { ...current, prepClaims: [...(current.prepClaims || []), newPrepClaim(fields)] } : current));
    toast.success(`${fields.client || "Prep claim"} added to this week's preparation claims. Choose the prep fee there.`);
  };
  const addPrepFromAtlas = (claim) => addPrepClaim({
    client: claim.client,
    grant: claim.grant || "",
    amount: claim.amount != null ? String(claim.amount) : undefined,
    note: claim.description ? `ATLAS: ${claim.description}` : "",
    source: "atlas",
  });
  const addPrepFromPending = (item) => addPrepClaim({
    client: item.client_name || "",
    courtLevel: courtLevelForCourt(item.court) || "Magistrates' / Children's Court",
    note: "Aid approved",
    source: "aid-pending",
  });

  const clearAtlasComparison = () => {
    setResult(current => {
      if (!current) return current;
      const next = { ...current };
      delete next.atlasClaims;
      return next;
    });
  };

  const scrollToAppearance = (id) => {
    document.getElementById(`appearance-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    setError(null);
    setLoading(false);
    setActions({});
    setNotes({});
    setFees({});
    setAid({});
    setAidFilter("All");
    setAutoSavedAt(null);
    clientStorage.removeItem("lacw_billing_autosave");
    setRecoveredDraft(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const { all, claimable, displayed, groupedByDate, mainGroups, lGroups, allActioned } = useMemo(() => {
    if (!result?.entries) return { all: [], claimable: [], displayed: [], groupedByDate: [], mainGroups: [], lGroups: [], allActioned: false };

    const all = result.entries;
    const claimable = all.filter(e => e.claimable !== false);
    const displayed = all;

    // The week by appearance date: all of it (for the printed sheet), and on screen the
    // VU, VC and unmarked appearances first, then the L-marked ones below them (isLMarked).
    const byDate = (entries) => {
    const dateGroups = new Map();
    entries.forEach(entry => {
      const dateKey = entry.date || "";
      if (!dateGroups.has(dateKey)) dateGroups.set(dateKey, []);
      dateGroups.get(dateKey).push(entry);
    });

    return Array.from(dateGroups, ([date, dateEntries]) => {
      const clientGroups = [];
      const seenClients = new Map();
      dateEntries.forEach(entry => {
        const clientKey = (entry.client_name || "Unknown").trim().toUpperCase();
        if (!seenClients.has(clientKey)) {
          seenClients.set(clientKey, clientGroups.length);
          clientGroups.push({ clientName: entry.client_name || "Unknown", grantType: entry.grant_type, entries: [] });
        }
        clientGroups[seenClients.get(clientKey)].entries.push(entry);
      });
      return { date, label: formatAppearanceDate(date), entries: dateEntries, clientGroups };
    }).sort((a, b) => {
      const dateDifference = appearanceDateSortValue(a.date) - appearanceDateSortValue(b.date);
      return dateDifference || a.label.localeCompare(b.label);
    });
    };
    const groupedByDate = byDate(displayed);
    const mainGroups = byDate(displayed.filter(entry => !isLMarked(entry.grant_type)));
    const lGroups = byDate(displayed.filter(entry => isLMarked(entry.grant_type)));

    const allActioned = displayed.length > 0 && displayed.every(e => !!actions[e.id]);

    return { all, claimable, displayed, groupedByDate, mainGroups, lGroups, allActioned };
  }, [result?.entries, actions]);

  // Appearances marked L on the diary start as LACW funded - in a freshly processed diary
  // and in a reopened week - unless an action has already been chosen.
  useEffect(() => {
    if (Object.keys(lacwFundedDefaults(result?.entries, actions)).length === 0) return;
    setActions(prev => ({ ...prev, ...lacwFundedDefaults(result?.entries, prev) }));
  }, [result?.entries, actions]);

  // --- Legal aid status (what the LACW Aid Status Diary held) ------------------------------

  // LACW's Aid Planner matters, to fill each appearance's aid status from.
  const [aidMatters, setAidMatters] = useState(() => listLocalMatters("lacw"));
  useEffect(() => {
    let active = true;
    syncMattersWithPortal("lacw").finally(() => { if (active) setAidMatters(listLocalMatters("lacw")); });
    return () => { active = false; };
  }, []);
  const aidPlannerSuggestions = useMemo(
    () => buildAidSuggestions(all, aid, aidMatters, { availabilityFor: getClaimAvailabilityTable }),
    [all, aid, aidMatters]
  );
  useEffect(() => {
    setAid(prev => fillAidFromPlanner(all, prev, aidPlannerSuggestions));
  }, [all, aidPlannerSuggestions]);

  const aidReminders = useMemo(() => diaryReminders(diaryRows(all, aid), { source: "lacw" }), [all, aid]);
  const markAidApplied = (reminder) => handleAidChange(reminder.entryId, { grantActionLodged: true, grantActionLodgedAt: new Date().toISOString() });
  const weekAidStats = useMemo(() => aidStats(all, aid), [all, aid]);

  // The week on screen, narrowed by the aid filter. Printing and the summary still use the
  // whole week.
  const { visibleGroups, visibleLGroups } = useMemo(() => {
    const keep = (entry) => matchesAidFilter(diaryRow(entry, aid), aidFilter);
    const filtered = (groups) => (aidFilter === "All" ? groups : groups
      .map(dateGroup => {
        const clientGroups = dateGroup.clientGroups
          .map(group => ({ ...group, entries: group.entries.filter(keep) }))
          .filter(group => group.entries.length);
        return { ...dateGroup, entries: dateGroup.entries.filter(keep), clientGroups };
      })
      .filter(dateGroup => dateGroup.entries.length));
    return { visibleGroups: filtered(mainGroups), visibleLGroups: filtered(lGroups) };
  }, [mainGroups, lGroups, aid, aidFilter]);
  const visibleLCount = visibleLGroups.reduce((count, dateGroup) => count + dateGroup.entries.length, 0);

  // LACW see each week's aid status (never its fees, actions or notes) and the held lists,
  // in their own portal. Both are written under LACW, and only an administrator can write them.
  const [aidShare, setAidShare] = useState({ state: "idle", at: null });
  const lastAidWeekRef = useRef("");
  const aidWeekIdRef = useRef(null);
  useEffect(() => {
    aidWeekIdRef.current = result?.aidWeekId || null;
  }, [result?.aidWeekId]);
  useEffect(() => {
    if (!all.length || weekPastRetention(all)) return;
    const weekLabel = getDraftDateRange(all);
    const formData = publishedAidWeek(all, aid, weekLabel);
    const signature = JSON.stringify({ ...formData, publishedAt: undefined });
    if (signature === lastAidWeekRef.current) return;
    const timer = setTimeout(async () => {
      const id = aidWeekIdRef.current
        || (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function" ? crypto.randomUUID() : null);
      if (!id) return;
      setAidShare(current => ({ ...current, state: "saving" }));
      try {
        const response = await fetch("/api/vals-drafts", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id, form_type: AID_WEEK_FORM_TYPE, draft_name: weekLabel, brand: "lacw", form_data: formData }),
        });
        if (!response.ok) throw new Error(`Sharing the aid status returned ${response.status}`);
        lastAidWeekRef.current = signature;
        if (!aidWeekIdRef.current) {
          aidWeekIdRef.current = id;
          setResult(current => (current && !current.aidWeekId ? { ...current, aidWeekId: id } : current));
        }
        setAidShare({ state: "shared", at: new Date() });
      } catch (err) {
        console.warn("The week's aid status could not be shared with LACW:", err);
        setAidShare(current => ({ ...current, state: "error" }));
      }
    }, 2500);
    return () => clearTimeout(timer);
  }, [all, aid]);

  const lastHeldListsRef = useRef("");
  useEffect(() => {
    if (!pendingAidLoaded) return;
    const formData = publishedHeldLists(pendingAidItems);
    const signature = JSON.stringify(formData.items);
    if (signature === lastHeldListsRef.current) return;
    const timer = setTimeout(async () => {
      try {
        const response = await fetch("/api/vals-drafts", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: HELD_LISTS_DRAFT_ID, form_type: HELD_LISTS_FORM_TYPE, draft_name: "Held lists", brand: "lacw", form_data: formData }),
        });
        if (!response.ok) throw new Error(`Sharing the held lists returned ${response.status}`);
        lastHeldListsRef.current = signature;
      } catch (err) {
        console.warn("The held lists could not be shared with LACW:", err);
      }
    }, 2500);
    return () => clearTimeout(timer);
  }, [pendingAidItems, pendingAidLoaded]);

  // Claimed appearances (an appearance type, Claimed, or Billed and closed) with no fee to claim.
  const claimsWithoutFee = useMemo(
    () => all.filter(entry => isClaimedAction(actions[entry.id]) && billingFeeFor(entry, actions[entry.id], fees[entry.id]) == null),
    [all, actions, fees]
  );

  const getActionColor = (name) => {
    if (name === "No Action") return "#94a3b8";
    const colorMap = {
      "Claimed": "#10b981",
      "Billed and closed": "#6366f1",
      "Not claimed": "#64748b",
      "Counsel appeared": "#0ea5e9",
      "LACW funded": "#a855f7",
      "Aid required": "#ef4444",
      "Extension required": "#f97316",
      "Aid pending": "#f59e0b",
      "To be claimed": "#06b6d4",
      "ACF lodged": "#14b8a6",
      "Query": "#3b82f6",
    };
    return colorMap[name] || "#3b82f6";
  };

  const renderReportsView = () => {
    return (
      <div className="space-y-6 animate-fade-in print:hidden">
        {/* Reports Header & Export */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-gray-800 p-6 rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm">
          <div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              LACW Billing Reporting & Analytics
            </h2>
            <p className="text-xs text-slate-400 mt-1">
              Aggregate data and workload statistics across all saved weeks ({uniqueWeeks.length} weeks total)
            </p>
          </div>
          <button
            onClick={handleExportCSV}
            className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-4 py-2.5 rounded-xl transition-all shadow-md shadow-blue-100 dark:shadow-none self-start sm:self-auto"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>

        {/* Two-Column Responsive Layout */}
        <div className="flex flex-col lg:flex-row gap-8 items-start">
          {/* Left Column: Interactive Saved Weeks Sidebar */}
          <div className="w-full lg:w-80 flex-shrink-0 space-y-4 print:hidden">
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 p-5 shadow-sm space-y-4">
              <div className="flex items-center justify-between border-b border-slate-100 dark:border-gray-700 pb-3">
                <h3 className="font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2 text-sm">
                  <FolderOpen className="w-4.5 h-4.5 text-blue-600" />
                  Billing Weeks
                </h3>
                <span className="text-[11px] bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2.5 py-0.5 rounded-full font-bold">
                  {cloudDrafts?.length || 0}
                </span>
              </div>

              {isDraftLoading && cloudDrafts?.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                </div>
              ) : !cloudDrafts || cloudDrafts.length === 0 ? (
                <div className="text-center py-10 px-4 text-slate-400 dark:text-slate-500 text-xs">
                  <p className="font-medium">No saved weeks yet</p>
                  <p className="mt-1 text-[11px] text-slate-400">Processed weeks will appear here when saved.</p>
                </div>
              ) : (
                <div className="space-y-1.5 max-h-[450px] overflow-y-auto pr-1">
                  {/* All Weeks Button */}
                  <button
                    onClick={() => setFilterWeek("all")}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-bold transition-all text-left ${
                      filterWeek === "all"
                        ? "bg-blue-600 text-white shadow-sm"
                        : "text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700"
                    }`}
                  >
                    <span>All Weeks</span>
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full ${
                        filterWeek === "all"
                          ? "bg-blue-500 text-white"
                          : "bg-slate-100 dark:bg-gray-700 text-slate-600 dark:text-slate-400"
                      }`}
                    >
                      {aggregatedEntries.length}
                    </span>
                  </button>

                  {/* Individual Weeks Buttons */}
                  {cloudDrafts.map((draft) => {
                    const count = draft.form_data?.result?.entries?.length || 0;
                    const isActive = filterWeek === draft.draft_name;

                    return (
                      <button
                        key={draft.id}
                        onClick={() => setFilterWeek(draft.draft_name)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-bold transition-all text-left ${
                          isActive
                            ? "bg-blue-600 text-white shadow-sm"
                            : "text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700"
                        }`}
                      >
                        <span className="truncate pr-2" title={draft.draft_name}>
                          {draft.draft_name}
                        </span>
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 ${
                            isActive
                              ? "bg-blue-500 text-white"
                              : "bg-slate-100 dark:bg-gray-700 text-slate-600 dark:text-slate-400"
                          }`}
                        >
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Filters and Data Output */}
          <div className="flex-1 min-w-0 w-full space-y-6">
            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Report Picker */}
                <div className="space-y-1">
                  <label htmlFor="billing-report-type" className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Report</label>
                  <select
                    id="billing-report-type"
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                    className="w-full text-xs font-bold text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-900/50 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {REPORT_TYPES.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                {/* Action Status Filter */}
                <div className="space-y-1">
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Action Status</label>
                  <select
                    value={filterAction}
                    onChange={(e) => setFilterAction(e.target.value)}
                    className="w-full text-xs font-semibold text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-gray-700 border border-slate-200 dark:border-gray-700 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="all">All Actions</option>
                    <option value="No Action">No Action Set</option>
                    {REPORT_ACTION_OPTIONS.filter(o => o.value).map((opt, idx) => (
                      <option key={idx} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Lawyer Filter */}
                <div className="space-y-1">
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Lawyer Initials</label>
                  <select
                    value={filterLawyer}
                    onChange={(e) => setFilterLawyer(e.target.value)}
                    className="w-full text-xs font-semibold text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-gray-700 border border-slate-200 dark:border-gray-700 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="all">All Lawyers</option>
                    {uniqueLawyers.map((lw, idx) => (
                      <option key={idx} value={lw}>{lw}</option>
                    ))}
                  </select>
                </div>

                {/* Search Filter */}
                <div className="space-y-1">
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Search</label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Client, court, notes, outcome..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full text-xs font-semibold text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-gray-700 border border-slate-200 dark:border-gray-700 rounded-xl pl-9 pr-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                  </div>
                </div>
              </div>
            </div>

            {/* KPI Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Total Appearances */}
              <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm flex items-center gap-4">
                <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-xl">
                  <Briefcase className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Total Appearances</p>
                  <h3 className="text-2xl font-black text-slate-800 dark:text-white mt-1">{stats.total}</h3>
                </div>
              </div>

              {/* Claimed */}
              <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm flex items-center gap-4">
                <div className="p-3 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl">
                  <CheckSquare className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Claimed</p>
                  <h3 className="text-2xl font-black text-slate-800 dark:text-white mt-1">{stats.claimedOrBilled}</h3>
                </div>
              </div>

              {/* Counsel Briefed */}
              <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm flex items-center gap-4">
                <div className="p-3 bg-sky-50 dark:bg-sky-900/30 rounded-xl">
                  <Users className="w-6 h-6 text-sky-600 dark:text-sky-400" />
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Counsel Briefed</p>
                  <h3 className="text-2xl font-black text-slate-800 dark:text-white mt-1">{stats.counsel}</h3>
                </div>
              </div>

              {/* LACW Funded */}
              <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm flex items-center gap-4">
                <div className="p-3 bg-purple-50 dark:bg-purple-900/30 rounded-xl">
                  <TrendingUp className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">LACW Funded</p>
                  <h3 className="text-2xl font-black text-slate-800 dark:text-white mt-1">{stats.funded}</h3>
                </div>
              </div>
            </div>

            {reportType !== "overview" && <BillingReportPanel report={report} onOpenWeek={handleLoadDraftFromReports} />}

            {reportType === "overview" && (<>
            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Action Status Chart */}
              <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm">
                <h3 className="font-bold text-slate-700 dark:text-slate-200 text-sm mb-4">Appearances by Action Status</h3>
                {actionCounts.length === 0 ? (
                  <div className="flex items-center justify-center h-[260px] text-xs text-slate-400">
                    No data available
                  </div>
                ) : (
                  <div className="h-[260px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={actionCounts} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                        <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#94a3b8" }} />
                        <YAxis tick={{ fontSize: 9, fill: "#94a3b8" }} />
                        <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                          {actionCounts.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={getActionColor(entry.name)} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* Lawyer Workload Chart */}
              <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm">
                <h3 className="font-bold text-slate-700 dark:text-slate-200 text-sm mb-4">Workload by Lawyer (Top 8)</h3>
                {lawyerCounts.length === 0 ? (
                  <div className="flex items-center justify-center h-[260px] text-xs text-slate-400">
                    No data available
                  </div>
                ) : (
                  <div className="h-[260px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={lawyerCounts.slice(0, 8)} layout="vertical" margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <XAxis type="number" tick={{ fontSize: 9, fill: "#94a3b8" }} />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 9, fill: "#94a3b8" }} />
                        <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                        <Bar dataKey="value" fill="#6366f1" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>

            {/* Appearances Table */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-slate-100 dark:border-gray-700 flex items-center justify-between">
                <h3 className="font-bold text-slate-800 dark:text-slate-200 text-sm">
                  Appearances Log ({filteredEntries.length} entries matching)
                </h3>
              </div>
              {filteredEntries.length === 0 ? (
                <div className="p-12 text-center text-slate-400 dark:text-slate-500 text-xs">
                  No entries match the active filters
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="bg-slate-50 dark:bg-gray-700 text-slate-400 dark:text-slate-400 font-bold uppercase tracking-wider text-[10px] border-b border-slate-100 dark:border-gray-700">
                        <th className="p-3">Date</th>
                        <th className="p-3">Week</th>
                        <th className="p-3">Client</th>
                        <th className="p-3">Court</th>
                        <th className="p-3">Type</th>
                        <th className="p-3">Lawyer</th>
                        <th className="p-3">Action Status</th>
                        <th className="p-3 text-right">Fee</th>
                        <th className="p-3">Outcome</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
                      {filteredEntries.map((e, idx) => {
                        const actionClass = ACTION_STYLE[e.action] || "bg-slate-50 dark:bg-slate-900/30 text-slate-500 border-slate-200";
                        return (
                          <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-gray-700 transition-colors">
                            <td className="p-3 font-semibold whitespace-nowrap text-slate-600 dark:text-slate-300">{e.date}</td>
                            <td className="p-3">
                              <button
                                onClick={() => handleLoadDraftFromReports(e.weekLabel)}
                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-bold hover:underline whitespace-nowrap text-left"
                                title="Load this week's full billing sheet"
                              >
                                {e.weekLabel}
                              </button>
                            </td>
                            <td className="p-3 font-bold text-slate-800 dark:text-slate-200">{e.client_name}</td>
                            <td className="p-3 text-slate-500 dark:text-slate-400 whitespace-nowrap truncate max-w-[150px]" title={e.court}>{e.court}</td>
                            <td className="p-3 whitespace-nowrap text-slate-600 dark:text-slate-300">{e.billingType}</td>
                            <td className="p-3 font-bold text-slate-600 dark:text-slate-400">{e.lawyer_initials || "—"}</td>
                            <td className="p-3 whitespace-nowrap">
                              <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border ${actionClass}`}>
                                {e.action}
                              </span>
                            </td>
                            <td className="p-3 whitespace-nowrap text-right font-semibold text-slate-700 dark:text-slate-300">{formatMoney(e.fee)}</td>
                            <td className="p-3 text-slate-500 dark:text-slate-400 truncate max-w-[200px]" title={e.outcome}>{e.outcome || "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            </>)}
          </div>
        </div>
      </div>
    );
  };

  const renderPendingAidView = (listAction = "Aid pending") => {
    const list = HELD_LISTS[listAction];
    const sortedItems = pendingAidItems.filter(item => (item.action || "Aid pending") === listAction && !item.feeSlipDoneAt).sort((a, b) => {
      const receivedFirst = Number(Boolean(b.feeSlip)) - Number(Boolean(a.feeSlip));
      if (receivedFirst) return receivedFirst;
      const dateDifference = appearanceDateSortValue(a.date) - appearanceDateSortValue(b.date);
      return dateDifference || String(a.client_name || "").localeCompare(String(b.client_name || ""));
    });

    return (
      <div className="space-y-5 animate-fade-in print:hidden">
        <div className={`rounded-2xl border p-6 shadow-sm ${list.banner}`}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className={`flex items-center gap-2 text-xl font-bold ${list.heading}`}>
                <AlertCircle className={`h-5 w-5 ${list.icon}`} />
                {list.title}
              </h2>
              <p className={`mt-1 text-sm ${list.body}`}>
                {list.description}
              </p>
            </div>
            <div className={`flex items-center gap-2 self-start rounded-full border bg-white px-3 py-1.5 text-xs font-bold ${list.pill}`}>
              {pendingAidSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : pendingAidSyncError ? <AlertCircle className="h-3.5 w-3.5 text-amber-600" /> : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
              {pendingAidSaving ? "Saving..." : pendingAidSyncError ? "Session recovery active" : "Saved securely"}
            </div>
          </div>
        </div>

        {!pendingAidLoaded ? (
          <div className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-16 text-sm font-semibold text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading {list.title} matters...
          </div>
        ) : sortedItems.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
            <h3 className="mt-3 text-base font-bold text-slate-800">{list.empty}</h3>
            <p className="mt-1 text-sm text-slate-500">Choose “{listAction}” on any billing row and it will appear here automatically.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedItems.map(item => (
              <div key={item.pendingKey} className={`rounded-2xl border bg-white p-5 shadow-sm ${list.card}`}>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-bold text-slate-900">{item.client_name || "Client name not provided"}</span>
                      {listAction === "Counsel appeared" && (item.feeSlip ? (
                        <span className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-xs font-bold text-emerald-700">Fee slip received</span>
                      ) : daysSince(item.date) != null && (
                        <span className="rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs font-bold text-sky-700">Waiting {daysSince(item.date)} day{daysSince(item.date) === 1 ? "" : "s"}</span>
                      ))}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
                      <span><strong className="text-slate-700">Date:</strong> {item.date || "Not provided"}</span>
                      <span><strong className="text-slate-700">Court:</strong> {item.court || "Not provided"}</span>
                      <span><strong className="text-slate-700">Carriage:</strong> {item.lawyer_initials || "Not provided"}</span>
                      {listAction === "Counsel appeared" && counselNameFor(item) && (
                        <span><strong className="text-slate-700">Counsel:</strong> {counselNameFor(item)}</span>
                      )}
                    </div>
                    {listAction === "Counsel appeared" && item.feeSlip && (
                      <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                        <p className="font-semibold">{item.feeSlip.subject || "Fee slip"}</p>
                        <p className="mt-0.5 text-xs text-emerald-800">
                          Received {item.feeSlip.receivedAt ? new Date(item.feeSlip.receivedAt).toLocaleDateString("en-AU") : "—"}
                          {item.feeSlip.amount != null && ` · $${item.feeSlip.amount.toFixed(2)}`}
                        </p>
                        {(() => {
                          const links = feeSlipLinks(item.feeSlip, intakeFeeSlips?.find(row => row.id === item.feeSlip.id));
                          return (
                            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs font-bold">
                              {links.slipUrl
                                ? <a href={links.slipUrl} target="_blank" rel="noreferrer" className="text-emerald-800 underline hover:text-emerald-950">Open fee slip</a>
                                : <span className="font-medium text-emerald-700">Fee slip file not stored</span>}
                              <a href={links.backsheetUrl} target="_blank" rel="noreferrer" className="text-emerald-800 underline hover:text-emerald-950" title="Opens the backsheet from the same email, if one was attached">Open backsheet</a>
                            </div>
                          );
                        })()}
                        <div className="mt-2 flex flex-wrap gap-2">
                          <button type="button" onClick={() => handleFeeSlipDone(item)} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700">Done, remove from list</button>
                          <button type="button" onClick={() => handleFeeSlipWrongMatch(item)} className="rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-bold text-emerald-800 hover:bg-emerald-100">Not this fee slip</button>
                        </div>
                      </div>
                    )}
                    <p className="mt-3 text-sm text-slate-700"><strong>Outcome:</strong> {item.outcome || "Further information required."}</p>
                    <textarea
                      value={item.note || ""}
                      onChange={event => handlePendingNoteChange(item, event.target.value)}
                      placeholder="Add an action note, aid reference or follow-up date..."
                      rows={2}
                      className="mt-3 w-full resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200"
                    />
                  </div>
                  <div className="w-full lg:w-56">
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">{list.updateLabel}</label>
                    <select
                      value={listAction}
                      onChange={event => handlePendingActionChange(item, event.target.value)}
                      className={`w-full cursor-pointer rounded-xl border px-3 py-2 text-sm font-bold focus:outline-none focus:ring-2 ${ACTION_STYLE[listAction]}`}
                    >
                      {ACTION_OPTIONS.filter(option => option.value).map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                    <p className="mt-2 text-xs leading-relaxed text-slate-500">Changing this removes the matter from this list.</p>
                    {(listAction === "Aid pending" || listAction === "To be claimed") && (
                      <button
                        type="button"
                        onClick={() => addPrepFromPending(item)}
                        className="mt-3 w-full rounded-xl border border-purple-300 bg-white px-3 py-2 text-xs font-bold text-purple-800 hover:bg-purple-50"
                        title={result ? "Adds this client to the preparation claims of the week on screen" : "Open or process this week's billing sheet first"}
                      >
                        Aid approved – add prep claim to this week
                      </button>
                    )}
                    {listAction === "Counsel appeared" && !item.feeSlip && (
                      <button type="button" onClick={() => handleFeeSlipDone(item)} className="mt-3 w-full rounded-xl border border-sky-300 bg-white px-3 py-2 text-xs font-bold text-sky-800 hover:bg-sky-50">Fee slip received, remove</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderOutlookIntakeView = () => (
    <div className="space-y-5 animate-fade-in print:hidden">
      <div className="rounded-2xl border border-blue-200 bg-blue-50 p-6 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="flex items-center gap-2 text-xl font-bold text-blue-950">
              <CalendarDays className="h-5 w-5 text-blue-600" />
              Portal Appearances
            </h2>
            <p className="mt-1 text-sm text-blue-800">
              Appearances saved in the LACW Mail & Calendar workspace arrive here automatically for billing review.
            </p>
          </div>
          <button
            type="button"
            onClick={loadOutlookIntake}
            disabled={outlookIntakeLoading}
            className="flex items-center justify-center gap-2 rounded-xl border border-blue-300 bg-white px-3 py-2 text-xs font-bold text-blue-800 hover:bg-blue-100 disabled:opacity-50"
          >
            {outlookIntakeLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
            Refresh
          </button>
        </div>
        {outlookIntakeSyncError && (
          <p className="mt-3 text-xs font-medium text-amber-800">
            The portal queue could not be reached. Calendar and other locally held entries are still shown below.
          </p>
        )}
      </div>

      {outlookIntakeLoading ? (
        <div className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-16 text-sm font-semibold text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading portal appearances...
        </div>
      ) : outlookIntake.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
          <h3 className="mt-3 text-base font-bold text-slate-800">No portal appearances are waiting</h3>
          <p className="mt-1 text-sm text-slate-500">Saving an appearance in LACW Mail & Calendar will add it here automatically.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {outlookIntake.map((draft) => {
            const entry = draft.form_data?.entry || {};
            return (
              <div key={draft.id} className="rounded-2xl border border-blue-200 bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-bold text-slate-900">{entry.client_name || "Client name not provided"}</span>
                      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-bold text-blue-800">{entry.grant_type || "Funding not provided"}</span>
                      {draft._intakeOrigin === "local" && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800">{draft.form_data?.source === "microsoft_calendar" ? "Microsoft calendar" : "Demo"}</span>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
                      <span><strong className="text-slate-700">Date:</strong> {entry.date || "Not provided"}</span>
                      <span><strong className="text-slate-700">Court:</strong> {entry.court || "Not provided"}</span>
                      <span><strong className="text-slate-700">Appearance:</strong> {entry.appearance_type || "Not provided"}</span>
                      <span><strong className="text-slate-700">Lawyer:</strong> {entry.lawyer_initials || "Not provided"}</span>
                    </div>
                    <p className="mt-3 text-sm text-slate-700"><strong>Outcome:</strong> {entry.outcome || "Further information required."}</p>
                    {entry.next_date && <p className="mt-1 text-sm text-slate-700"><strong>Next court date:</strong> {entry.next_date}</p>}
                    {entry.notes && <pre className="mt-3 whitespace-pre-wrap font-sans text-xs leading-5 text-slate-500">{entry.notes}</pre>}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleImportOutlookEntry(draft)}
                    className="rounded-xl bg-blue-600 px-4 py-2.5 text-xs font-bold text-white hover:bg-blue-700"
                  >
                    {result ? "Add to current week" : "Start billing sheet"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // One appearance date and its appearances, as the billing list shows it. Used for the main
  // list and again for the L-marked appearances below it.
  const renderDateGroup = (dateGroup, keyPrefix = "") => (
    <section key={`${keyPrefix}${dateGroup.date || "missing-date"}`} className="overflow-hidden rounded-2xl border border-sky-200 bg-sky-50/40 shadow-sm dark:border-sky-900/40 dark:bg-sky-950/10">
      <div className="flex items-center justify-between gap-4 border-b border-sky-200 bg-gradient-to-r from-sky-700 to-blue-700 px-4 py-3 text-white dark:border-sky-900/50">
        <div className="flex items-center gap-2.5">
          <CalendarDays className="h-5 w-5 text-sky-100" />
          <div>
            <h3 className="text-sm font-bold">{dateGroup.label}</h3>
            <p className="text-[10px] font-medium text-sky-100">Appearance date</p>
          </div>
        </div>
        <span className="rounded-full border border-white/30 bg-white/15 px-2.5 py-1 text-[10px] font-bold">
          {dateGroup.entries.length} appearance{dateGroup.entries.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="space-y-3 p-3 sm:p-4">
        {dateGroup.clientGroups.map((group, groupIndex) => (
          group.entries.length === 1
            ? <AppearanceRow key={group.entries[0].id} entry={group.entries[0]} action={actions[group.entries[0].id]} note={notes[group.entries[0].id] || ""} fee={fees[group.entries[0].id]} aid={aidFor(aid, group.entries[0].id)} aidSuggestion={aidPlannerSuggestions.get(group.entries[0].id)} onActionChange={handleActionChange} onNoteChange={handleNoteChange} onFeeChange={handleFeeChange} onAidChange={handleAidChange} onUseAidSuggestion={handleUseAidSuggestion} />
            : <ClientGroup key={`${dateGroup.date}-${groupIndex}`} clientName={group.clientName} grantType={group.grantType} entries={group.entries} actions={actions} notes={notes} fees={fees} aid={aid} aidSuggestions={aidPlannerSuggestions} onActionChange={handleActionChange} onNoteChange={handleNoteChange} onFeeChange={handleFeeChange} onAidChange={handleAidChange} onUseAidSuggestion={handleUseAidSuggestion} />
        ))}
      </div>
    </section>
  );

  return (
    <div className={`lacw-billing-print-root flex flex-col ${isPortalMode ? "" : "min-h-screen bg-slate-50"}`}>
      <style>{`
        .lacw-billing-print-root,
        .lacw-billing-print-root * {
          font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif !important;
          font-size: 13px !important;
          line-height: 1.4 !important;
          font-variant-ligatures: none;
          font-synthesis: none;
        }
${BILLING_PRINT_CSS}
      `}</style>
      {!isPortalMode && (
        <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between gap-4 sticky top-0 z-10 shadow-sm">
          <a href={createPageUrl("Home")} className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors text-sm font-medium">
            <ArrowLeft className="w-4 h-4" />
            Back to Portal
          </a>
          <div className="text-center">
            <p className="font-semibold text-slate-800 text-sm">Billing Sheet</p>
            <p className="text-xs text-slate-400">Diary to ATLAS claim processor</p>
            <div className="mt-1 inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
              Parser {BILLING_PARSER_VERSION}
            </div>
          </div>
          {result ? (
            <button onClick={reset} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm font-medium transition-colors">
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
          ) : <div className="w-20" />}
        </div>
      )}

      <div className="flex-1 w-full max-w-7xl mx-auto px-4 py-8 pb-24 print:p-0 print:overflow-visible">
        {/* Navigation Tabs */}
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 dark:border-gray-700 pb-4 mb-6 print:hidden">
          <button
            onClick={() => setActiveView("billing")}
            className={`whitespace-nowrap px-4 py-2 text-sm font-bold rounded-xl transition-all ${
              activeView === "billing"
                ? "bg-blue-600 text-white shadow-md shadow-blue-100 dark:shadow-none"
                : "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-gray-800"
            }`}
          >
            Billing Processor
          </button>
          <button
            onClick={() => setActiveView("reports")}
            className={`whitespace-nowrap px-4 py-2 text-sm font-bold rounded-xl transition-all ${
              activeView === "reports"
                ? "bg-blue-600 text-white shadow-md shadow-blue-100 dark:shadow-none"
                : "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-gray-800"
            }`}
          >
            Reporting Dashboard
          </button>
          <button
            onClick={() => setActiveView("outlook-intake")}
            className={`flex items-center gap-2 whitespace-nowrap px-4 py-2 text-sm font-bold rounded-xl transition-all ${
              activeView === "outlook-intake"
                ? "bg-blue-600 text-white shadow-md shadow-blue-100 dark:shadow-none"
                : "text-slate-600 hover:bg-blue-50 dark:text-slate-400 dark:hover:bg-gray-800"
            }`}
          >
            Outlook Intake
            <span className={`rounded-full px-2 py-0.5 text-[11px] ${activeView === "outlook-intake" ? "bg-white/20 text-white" : "bg-blue-100 text-blue-800"}`}>
              {outlookIntake.length}
            </span>
          </button>
          <button
            onClick={() => setActiveView("pending-aid")}
            className={`flex items-center gap-2 whitespace-nowrap px-4 py-2 text-sm font-bold rounded-xl transition-all ${
              activeView === "pending-aid"
                ? "bg-amber-500 text-white shadow-md shadow-amber-100 dark:shadow-none"
                : "text-slate-600 hover:bg-amber-50 dark:text-slate-400 dark:hover:bg-gray-800"
            }`}
          >
            Aid Pending
            <span className={`rounded-full px-2 py-0.5 text-[11px] ${activeView === "pending-aid" ? "bg-white/20 text-white" : "bg-amber-100 text-amber-800"}`}>
              {pendingAidItems.filter(item => (item.action || "Aid pending") === "Aid pending").length}
            </span>
          </button>
          <button
            onClick={() => setActiveView("to-be-claimed")}
            className={`flex items-center gap-2 whitespace-nowrap px-4 py-2 text-sm font-bold rounded-xl transition-all ${
              activeView === "to-be-claimed"
                ? "bg-cyan-600 text-white shadow-md shadow-cyan-100 dark:shadow-none"
                : "text-slate-600 hover:bg-cyan-50 dark:text-slate-400 dark:hover:bg-gray-800"
            }`}
          >
            To be claimed
            <span className={`rounded-full px-2 py-0.5 text-[11px] ${activeView === "to-be-claimed" ? "bg-white/20 text-white" : "bg-cyan-100 text-cyan-800"}`}>
              {pendingAidItems.filter(item => item.action === "To be claimed").length}
            </span>
          </button>
          <button
            onClick={() => setActiveView("awaiting-fee-slip")}
            className={`flex items-center gap-2 whitespace-nowrap px-4 py-2 text-sm font-bold rounded-xl transition-all ${
              activeView === "awaiting-fee-slip"
                ? "bg-sky-600 text-white shadow-md shadow-sky-100 dark:shadow-none"
                : "text-slate-600 hover:bg-sky-50 dark:text-slate-400 dark:hover:bg-gray-800"
            }`}
          >
            Awaiting fee slip
            <span className={`rounded-full px-2 py-0.5 text-[11px] ${activeView === "awaiting-fee-slip" ? "bg-white/20 text-white" : "bg-sky-100 text-sky-800"}`}>
              {pendingAidItems.filter(item => isAwaitingFeeSlip(item) && !item.feeSlip).length}
            </span>
            {pendingAidItems.some(item => isAwaitingFeeSlip(item) && item.feeSlip) && (
              <span className={`rounded-full px-2 py-0.5 text-[11px] ${activeView === "awaiting-fee-slip" ? "bg-white/20 text-white" : "bg-emerald-100 text-emerald-800"}`}>
                {pendingAidItems.filter(item => isAwaitingFeeSlip(item) && item.feeSlip).length} received
              </span>
            )}
          </button>
        </div>

        {activeView === "reports" ? (
          renderReportsView()
        ) : activeView === "outlook-intake" ? (
          renderOutlookIntakeView()
        ) : activeView === "pending-aid" ? (
          renderPendingAidView("Aid pending")
        ) : activeView === "to-be-claimed" ? (
          renderPendingAidView("To be claimed")
        ) : activeView === "awaiting-fee-slip" ? (
          renderPendingAidView("Counsel appeared")
        ) : (
          <div className="flex flex-col lg:flex-row gap-8 items-start">
            <input
              ref={archiveInputRef}
              type="file"
              accept=".json,.lacw-week.json,application/json"
              onChange={handleLoadSavedWeek}
              className="hidden"
            />
            <div className="w-full lg:w-80 flex-shrink-0 space-y-4 print:hidden">
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 p-5 shadow-sm space-y-4">
                <div className="flex items-center justify-between border-b border-slate-100 dark:border-gray-700 pb-3">
                  <h3 className="font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2 text-sm animate-fade-in">
                    <FolderOpen className="w-4.5 h-4.5 text-blue-600" />
                    Saved Billing Weeks
                  </h3>
                  <span className="text-[11px] bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2.5 py-0.5 rounded-full font-bold">
                    {cloudDrafts?.length || 0}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => archiveInputRef.current?.click()}
                  className="w-full flex items-center justify-center gap-2 rounded-xl border border-blue-300 bg-blue-50 px-3 py-2.5 text-xs font-bold text-blue-800 transition-colors hover:bg-blue-100 dark:border-blue-900/50 dark:bg-blue-950/20 dark:text-blue-300"
                >
                  <FolderOpen className="w-4 h-4" />
                  Open Saved Week
                </button>

                {isDraftLoading && cloudDrafts?.length === 0 ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                  </div>
                ) : !cloudDrafts || cloudDrafts.length === 0 ? (
                  <div className="text-center py-10 px-4 text-slate-400 dark:text-slate-500 text-xs">
                    <p className="font-medium">No weeks saved on this computer yet</p>
                    <p className="mt-1 text-[11px] text-slate-400">Choose Save Week after processing a diary. You can also open a backup from Downloads.</p>
                  </div>
                ) : (
                  <div className="overflow-hidden">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead>
                        <tr className="border-b border-slate-100 dark:border-gray-700 text-slate-400 font-bold uppercase tracking-wider text-[10px]">
                          <th className="pb-2 font-bold w-1/2">Week</th>
                          <th className="pb-2 font-bold w-1/4">Saved On</th>
                          <th className="pb-2 font-bold text-right w-1/4">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
                        {cloudDrafts.map((draft) => {
                          const formattedDate = new Date(draft.updated_at || draft.created_at).toLocaleDateString("en-AU", {
                            day: "2-digit",
                            month: "2-digit"
                          });
                          const formattedTime = new Date(draft.updated_at || draft.created_at).toLocaleTimeString("en-AU", {
                            hour: "2-digit",
                            minute: "2-digit"
                          });
                          const savedBy = draft.saved_by || "Guest";
                          const isActive = result?.draftId === draft.id;

                          return (
                            <tr key={draft.id} className={`${isActive ? "bg-blue-50/50 dark:bg-blue-900/10 font-bold" : "hover:bg-slate-50/50 dark:hover:bg-gray-700"} transition-colors`}>
                              <td className={`py-2.5 pr-2 ${isActive ? "text-blue-700 dark:text-blue-400 font-bold" : "font-semibold text-slate-700 dark:text-slate-300"} max-w-[110px] truncate`} title={draft.draft_name}>
                                {draft.draft_name}
                              </td>
                              <td className="py-2.5 text-slate-400 dark:text-slate-500 whitespace-nowrap" title={`Saved by ${savedBy} at ${formattedTime}`}>
                                {formattedDate}
                              </td>
                              <td className="py-2.5 text-right whitespace-nowrap">
                                <div className="inline-flex items-center gap-1.5">
                                  <button
                                    onClick={() => handleLoadDraft(draft)}
                                    className="px-2 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded text-[11px] font-bold transition-colors"
                                  >
                                    Load
                                  </button>
                                  <button
                                    onClick={() => handleDeleteDraft(draft.id)}
                                    className="p-1 text-slate-400 hover:text-red-600 dark:hover:text-red-400 rounded transition-colors"
                                    title="Delete saved week"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 min-w-0 w-full space-y-6">
              {recoveredDraft && !result && (
                <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 rounded-2xl p-4 flex items-start justify-between gap-4 text-xs animate-fade-in print:hidden">
                  <div className="flex items-start gap-2.5">
                    <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-amber-900 dark:text-amber-300">Unsaved progress found</p>
                      <p className="text-amber-700 dark:text-amber-400 mt-0.5">We found unsaved changes for "{recoveredDraft.fileName}" from {new Date(recoveredDraft.dateSaved).toLocaleTimeString("en-AU")}.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleLoadAutosave(recoveredDraft)}
                      className="bg-amber-600 hover:bg-amber-700 text-white font-bold px-3 py-1.5 rounded-lg transition-colors"
                    >
                      Restore
                    </button>
                    <button
                      onClick={() => {
                        clientStorage.removeItem("lacw_billing_autosave");
                        setRecoveredDraft(null);
                      }}
                      className="text-amber-700 dark:text-amber-400 hover:text-amber-950 font-semibold px-2 py-1.5"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              )}

              {!result && (
                <div
                  onDrop={handleDrop}
                  onDragOver={e => e.preventDefault()}
                  className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-dashed border-slate-300 dark:border-gray-600 p-12 text-center hover:border-blue-500 dark:hover:border-blue-400 transition-colors cursor-pointer"
                  onClick={() => fileRef.current?.click()}
                >
                  <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={e => handleFile(e.target.files[0])} />
                  {file ? (
                    <div className="flex flex-col items-center gap-3">
                      <FileText className="w-12 h-12 text-blue-600" />
                      <p className="font-bold text-slate-800 dark:text-slate-200">{file.name}</p>
                      <p className="text-xs text-slate-400 dark:text-slate-500">{(file.size / 1024).toFixed(1)} KB · Click to change</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3">
                      <Upload className="w-12 h-12 text-slate-300 dark:text-slate-600" />
                      <p className="font-bold text-slate-700 dark:text-slate-300">Drop your diary PDF here</p>
                      <p className="text-xs text-slate-400 dark:text-slate-500">or click to browse · PDF files only</p>
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/40 rounded-xl p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                </div>
              )}

              {file && !result && !loading && (
                <button
                  onClick={handleProcess}
                  className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm transition-all shadow-md shadow-blue-100 dark:shadow-none"
                >
                  <FileText className="w-4 h-4" />
                  Process diary & extract claims
                </button>
              )}

              {loading && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 p-12 flex flex-col items-center gap-4">
                  <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
                  <p className="font-bold text-slate-700 dark:text-slate-300">Reading your diary PDF…</p>
                  <p className="text-xs text-slate-400 dark:text-slate-500">This uses AI to extract and classify each entry. May take 15–30 seconds.</p>
                </div>
              )}

              {result && (
                <>
                  <div className="bg-blue-50/70 dark:bg-blue-950/20 border border-blue-200/60 dark:border-blue-900/30 rounded-2xl p-4 flex flex-col gap-3">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-bold text-blue-900 dark:text-blue-300 flex flex-wrap items-center gap-2">
                          <span>{claimable.length} claimable entries · {all.length} total extracted</span>
                          {result.draftId && (
                            <span className="text-[10px] bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900/40 px-2 py-0.5 rounded-full font-bold whitespace-nowrap">
                              Saved on this computer
                            </span>
                          )}
                        </p>
                        {result.summary && (
                          <p className="text-[11px] text-blue-800 dark:text-blue-400/80 mt-0.5">
                            {result.summary}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 border-t border-blue-200/60 dark:border-blue-900/30 pt-3 print:hidden">
                      {cloudDrafts && cloudDrafts.length > 0 && (
                        <select
                          value={result.draftId || ""}
                          onChange={(e) => {
                            const selected = cloudDrafts.find(d => d.id === e.target.value);
                            if (selected) handleLoadDraft(selected);
                          }}
                          className="text-xs font-semibold text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-gray-600 rounded-xl px-2.5 py-2 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors bg-white dark:bg-gray-800 focus:outline-none"
                        >
                          <option value="" disabled>Switch week...</option>
                          {cloudDrafts.map(draft => (
                            <option key={draft.id} value={draft.id}>{draft.draft_name}</option>
                          ))}
                        </select>
                      )}
                      <button
                        onClick={handleSaveDraft}
                        className="flex items-center gap-1.5 text-xs font-bold text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-gray-600 rounded-xl px-3.5 py-2 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors bg-white dark:bg-gray-800"
                      >
                        <Save className="w-3.5 h-3.5 text-slate-500" />
                        Save Week
                      </button>
                      {autoSaveFailed ? (
                        <span role="alert" className="rounded-lg border border-red-300 bg-red-50 px-2 py-1 text-[11px] font-bold text-red-800">
                          Not saved on this computer. Press Save Week to download a backup.
                        </span>
                      ) : autoSavedAt && (
                        <span className="text-[11px] font-medium text-slate-400">
                          Auto-saved {autoSavedAt.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" })}
                        </span>
                      )}
                      <button
                        onClick={() => archiveInputRef.current?.click()}
                        className="flex items-center gap-1.5 text-xs font-bold text-blue-700 dark:text-blue-400 border border-blue-300 dark:border-blue-900/50 rounded-xl px-3.5 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      >
                        <FolderOpen className="w-3.5 h-3.5" />
                        Open Week
                      </button>
                      <button
                        onClick={handlePrint}
                        className="flex items-center gap-1.5 text-xs font-bold text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-gray-600 rounded-xl px-3.5 py-2 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors bg-white dark:bg-gray-800"
                      >
                        <Printer className="w-3.5 h-3.5" />
                        Print / Save PDF
                      </button>
                      <button
                        onClick={() => atlasInputRef.current?.click()}
                        className="flex items-center gap-1.5 text-xs font-bold text-blue-700 dark:text-blue-400 border border-blue-300 dark:border-blue-900/50 rounded-xl px-3.5 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                        title="Upload the claims spreadsheet run from ATLAS to compare it with this week"
                      >
                        <Upload className="w-3.5 h-3.5" />
                        Compare ATLAS claims
                      </button>
                      <input ref={atlasInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleAtlasUpload} />
                      <button
                        onClick={handleCopyAll}
                        className="flex items-center gap-1.5 text-xs font-bold text-blue-700 dark:text-blue-400 border border-blue-300 dark:border-blue-900/50 rounded-xl px-3.5 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      >
                        <Copy className="w-3.5 h-3.5" />
                        {copied ? "Copied!" : "Copy all"}
                      </button>
                      <button
                        onClick={reset}
                        className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 border border-slate-300 dark:border-gray-600 rounded-xl px-3.5 py-2 transition-colors"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        Reset
                      </button>
                    </div>
                  </div>

                  <LawyerQuestionsPanel
                    entries={all}
                    actions={actions}
                    notes={notes}
                    weekLabel={getDraftDateRange(all)}
                    onJump={scrollToAppearance}
                  />

                  <AtlasComparePanel
                    atlas={result.atlasClaims}
                    entries={all}
                    actions={actions}
                    fees={fees}
                    prepClaims={prepClaims}
                    onJump={scrollToAppearance}
                    onClear={clearAtlasComparison}
                    onAddPrep={addPrepFromAtlas}
                  />

                  {aidReminders.length > 0 && (
                    <GrantRemindersPanel
                      reminders={aidReminders}
                      onMarkApplied={markAidApplied}
                      flowchartHref={getPortalToolPath("lacw", "aid-extension-flowchart")}
                      guideHref={getPortalToolPath("lacw", "reminders-guide")}
                      className="print:hidden"
                    />
                  )}

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs print:hidden">
                    <label className="flex items-center gap-2 font-bold text-slate-600">
                      Aid status
                      <select
                        value={aidFilter}
                        onChange={e => setAidFilter(e.target.value)}
                        className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-300"
                      >
                        {AID_FILTERS.map(option => <option key={option} value={option}>{option === "All" ? "Show all" : option}</option>)}
                      </select>
                    </label>
                    <span className="text-slate-600"><strong className="text-slate-900">{weekAidStats.confirmed}</strong> of {weekAidStats.total} confirmed</span>
                    <span className="text-slate-600"><strong className="text-emerald-700">{weekAidStats.funded}</strong> funded</span>
                    <span className="text-slate-600"><strong className="text-amber-700">{weekAidStats.pending}</strong> aid pending</span>
                    <span className={`ml-auto font-semibold ${aidShare.state === "error" ? "text-red-700" : "text-slate-500"}`} role="status">
                      {aidShare.state === "error"
                        ? "LACW can't see this week's aid status yet: it could not be shared."
                        : aidShare.state === "saving"
                          ? "Sharing aid status with LACW…"
                          : aidShare.at
                            ? `Aid status shared with LACW ${aidShare.at.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" })}`
                            : "LACW see each appearance's aid status, not the billing."}
                    </span>
                  </div>

                  {claimsWithoutFee.length > 0 && (
                    <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 print:hidden" role="status">
                      <p className="flex items-center gap-2 text-sm font-bold text-amber-900">
                        <AlertCircle className="h-4 w-4 flex-shrink-0 text-amber-600" />
                        {claimsWithoutFee.length} claimed appearance{claimsWithoutFee.length === 1 ? " has" : "s have"} no fee allocated
                      </p>
                      <p className="mt-1 text-xs text-amber-800">Add a fee (or change the action) before printing, or they will print without a fee and be left out of the totals.</p>
                      <ul className="mt-2 flex flex-wrap gap-2">
                        {claimsWithoutFee.map(entry => (
                          <li key={entry.id}>
                            <button
                              type="button"
                              onClick={() => scrollToAppearance(entry.id)}
                              className="rounded-lg border border-amber-300 bg-white px-2.5 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100"
                            >
                              {entry.date} · {entry.client_name || "Client not named"} · {actions[entry.id]}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="space-y-6 print:hidden">
                    {aidFilter !== "All" && visibleGroups.length === 0 && visibleLGroups.length === 0 && (
                      <p className="rounded-2xl border border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-500">No appearances match this aid status.</p>
                    )}
                    {visibleGroups.map((dateGroup) => renderDateGroup(dateGroup))}
                    {visibleLGroups.length > 0 && (
                      <section aria-labelledby="l-marked-heading" className="space-y-4 border-t-2 border-dashed border-blue-200 pt-6">
                        <div>
                          <h3 id="l-marked-heading" className="text-sm font-bold text-blue-900">
                            Marked L ({visibleLCount} appearance{visibleLCount === 1 ? "" : "s"})
                          </h3>
                          <p className="text-xs text-slate-500">Appearances marked (L) on the diary, by date. The ones above are VU, VC or unmarked.</p>
                        </div>
                        {visibleLGroups.map((dateGroup) => renderDateGroup(dateGroup, "l-"))}
                      </section>
                    )}
                  </div>

                  {!all.length && (
                    <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 rounded-xl p-4 print:hidden">
                      <p className="text-sm text-amber-800 dark:text-amber-400">No diary entries could be extracted from this PDF. Check that the file is a text-based PDF (not a scanned image) and try again.</p>
                    </div>
                  )}

                  <div className="print:hidden">
                    <DiaryCodesLegend sheetHref={getPortalToolPath("lacw", "diary-codes")} />
                  </div>

                  <p className="text-xs text-slate-400 dark:text-slate-500 text-center print:hidden">AI extraction may not be 100% accurate. Always verify each entry against the original diary before submitting claims in ATLAS.</p>

                  <PrepClaimsPanel lines={prepClaims} onChange={setPrepClaims} />

                  {allActioned && <BillingSummary entries={displayed} actions={actions} notes={notes} onNotesChange={setNotes} />}

                  <BillingPrintSheet
                    fileName={file?.name}
                    weekLabel={getDraftDateRange(all)}
                    entries={all}
                    groupedByDate={groupedByDate}
                    actions={actions}
                    fees={fees}
                    notes={notes}
                    prepClaims={prepClaims}
                  />
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
